import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  getProducts, createProduct, updateProduct, deleteProduct, reorderProducts,
  getFactories, getShops,
  getCategories, createCategory, updateCategory, deleteCategory, reorderCategories,
} from '../../services/api';
import toast from 'react-hot-toast';

const dragHandleStyle = { cursor: 'grab', fontSize: 16, userSelect: 'none' };

// Greek-friendly compare: strips accents and case, so "παστα" finds "Πάστα"
const normalize = (str) => (str || '').toLocaleLowerCase('el-GR')
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

// Products with no category live in this pseudo-group, which always sits
// last. It is not a row in the database — category_id is simply NULL.
const UNCAT = { id: null, name: 'Χωρίς κατηγορία', virtual: true };

export default function Products() {
  const [items, setItems] = useState([]);          // flat, already in display order
  const [categories, setCategories] = useState([]);
  const [factories, setFactories] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [factoryFilter, setFactoryFilter] = useState('');
  const [search, setSearch] = useState('');
  // Categories start closed: with a few hundred products, an open list is
  // unreadable. Kept per browser so the admin's choice survives a reload.
  const [openCats, setOpenCats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('orders_openCats') || '{}'); }
    catch { return {}; }
  });
  const setOpen = (key, on) => {
    setOpenCats((o) => {
      const next = { ...o, [key]: on };
      try { localStorage.setItem('orders_openCats', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  // shop_ids holds the shops where it IS available. The backend stores the
  // opposite (exclusions), so everything ticked saves no rows at all and the
  // item stays visible in shops created later.
  const [form, setForm] = useState({ name: '', code: '', factory_ids: [], price: '', shop_ids: [], category_id: '' });
  const [saving, setSaving] = useState(false);

  // Category management
  const [showCatModal, setShowCatModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [catEditingId, setCatEditingId] = useState(null);
  const [catEditingName, setCatEditingName] = useState('');
  const [catShopFor, setCatShopFor] = useState(null);   // category whose shops are being set
  const [catShopIds, setCatShopIds] = useState([]);
  const [catBusy, setCatBusy] = useState(false);

  // ── Drag state ────────────────────────────────────────────
  // Kept in refs because React re-renders during a drag and would otherwise
  // throw the values away mid-gesture.
  const [draggingId, setDraggingId] = useState(null);
  const dragItem = useRef(null);
  const dragBackup = useRef(null);
  const dropDone = useRef(false);

  const [draggingCatId, setDraggingCatId] = useState(null);
  const dragCat = useRef(null);
  const catBackup = useRef(null);
  const catDropDone = useRef(false);

  // Dragging only makes sense against the full list — with a factory filter
  // on you are looking at a subset, and saving that order would scramble
  // the products you cannot see.
  const canDrag = !factoryFilter && !showInactive && !search.trim();

  // Lay the flat list out in display order: category by category in their
  // own order, each product keeping its relative position, uncategorised
  // last. Guarantees each group is contiguous after any move.
  const regroup = (list, cats) => {
    const out = [];
    for (const c of cats) out.push(...list.filter((p) => p.category_id === c.id));
    out.push(...list.filter((p) => !p.category_id));
    // Anything whose category vanished (deleted elsewhere) still belongs somewhere
    for (const p of list) if (!out.includes(p)) out.push(p);
    return out;
  };

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([
      getProducts({ includeInactive: showInactive, factoryId: factoryFilter || undefined }),
      getFactories(false),
      getShops(false),
      getCategories({ includeInactive: true }),
    ])
      .then(([pRes, fRes, sRes, cRes]) => {
        setCategories(cRes.data);
        setItems(regroup(pRes.data, cRes.data));
        setFactories(fRes.data);
        setShops(sRes.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive, factoryFilter]);

  // Groups for rendering: every category (so you can drop into an empty one)
  // plus the uncategorised bucket when it has anything in it.
  //
  // Search looks at the product name, the product code and the category
  // name. A category whose NAME matches keeps all of its products, so
  // typing a category name is a way to pull up that whole group.
  const groups = useMemo(() => {
    const q = normalize(search.trim());
    const catHit = (c) => !!q && !c.virtual && normalize(c.name).includes(q);
    const prodHit = (p) => !q
      || normalize(p.name).includes(q)
      || (p.code && normalize(p.code).includes(q));

    const pick = (c, list) => (catHit(c) ? list : list.filter(prodHit));

    const g = [];
    for (const c of categories) {
      const mine = items.filter((p) => p.category_id === c.id);
      const shown = pick(c, mine);
      // While searching, a category with nothing to show is just noise
      if (q && shown.length === 0 && !catHit(c)) continue;
      g.push({ cat: c, products: shown });
    }
    const orphans = items.filter((p) => !p.category_id).filter(prodHit);
    if (orphans.length > 0 || (!q && categories.length === 0)) {
      g.push({ cat: UNCAT, products: orphans });
    }
    return g;
  }, [items, categories, search]);

  const catKeyOf = (cat) => (cat.id === null ? 'uncat' : String(cat.id));

  const setAllOpen = (on) => {
    const next = {};
    for (const g of groups) next[catKeyOf(g.cat)] = on;
    setOpenCats(next);
    try { localStorage.setItem('orders_openCats', JSON.stringify(next)); } catch (e) {}
  };

  const visibleCount = useMemo(
    () => groups.reduce((n, g) => n + g.products.length, 0),
    [groups]
  );

  // ── Product modal ─────────────────────────────────────────
  const openCreate = (presetCategoryId = '') => {
    setEditing(null);
    setForm({
      name: '',
      code: '',
      factory_ids: factoryFilter ? [Number(factoryFilter)] : [],
      price: '',
      shop_ids: shops.map((s) => s.id),
      category_id: presetCategoryId === null ? '' : String(presetCategoryId || ''),
    });
    setShowModal(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    const hidden = p.hidden_shop_ids || [];
    setForm({
      name: p.name,
      code: p.code || '',
      factory_ids: (p.factories || []).map((f) => f.id),
      price: String(p.price),
      shop_ids: shops.filter((s) => !hidden.includes(s.id)).map((s) => s.id),
      category_id: p.category_id ? String(p.category_id) : '',
    });
    setShowModal(true);
  };

  const toggleFactory = (id) => {
    setForm((f) => ({
      ...f,
      factory_ids: f.factory_ids.includes(id)
        ? f.factory_ids.filter((x) => x !== id)
        : [...f.factory_ids, id],
    }));
  };
  const moveFactoryUp = (id) => {
    setForm((f) => {
      const i = f.factory_ids.indexOf(id);
      if (i <= 0) return f;
      const n = [...f.factory_ids];
      [n[i - 1], n[i]] = [n[i], n[i - 1]];
      return { ...f, factory_ids: n };
    });
  };
  const toggleShop = (id) => {
    setForm((f) => ({
      ...f,
      shop_ids: f.shop_ids.includes(id) ? f.shop_ids.filter((x) => x !== id) : [...f.shop_ids, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Συμπληρώστε όνομα');
    if (form.factory_ids.length === 0) return toast.error('Επιλέξτε τουλάχιστον ένα εργοστάσιο');
    const priceNum = parseFloat(String(form.price).replace(',', '.'));
    if (isNaN(priceNum) || priceNum < 0) return toast.error('Άκυρη τιμή');
    if (shops.length > 0 && form.shop_ids.length === 0) {
      return toast.error('Επιλέξτε τουλάχιστον ένα κατάστημα');
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),   // '' clears it server-side
        factory_ids: form.factory_ids,
        price: priceNum,
        hidden_shop_ids: shops.filter((s) => !form.shop_ids.includes(s.id)).map((s) => s.id),
        category_id: form.category_id ? Number(form.category_id) : null,
      };
      if (editing) { await updateProduct(editing.id, payload); toast.success('Ενημερώθηκε'); }
      else { await createProduct(payload); toast.success('Δημιουργήθηκε'); }
      setShowModal(false);
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSaving(false); }
  };

  const softDelete = async (p) => {
    if (!window.confirm(`Απενεργοποίηση: ${p.name};`)) return;
    try { await deleteProduct(p.id, false); toast.success('Απενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const reactivate = async (p) => {
    try { await updateProduct(p.id, { is_active: true }); toast.success('Ενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const hardDelete = async (p) => {
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ: ${p.name};\nΑυτή η ενέργεια δεν αναιρείται.`)) return;
    try { await deleteProduct(p.id, true); toast.success('Διαγράφηκε οριστικά'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  // ── Category actions ──────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    setCatBusy(true);
    try {
      await createCategory({ name: newCatName.trim() });
      setNewCatName('');
      toast.success('Η κατηγορία προστέθηκε');
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setCatBusy(false); }
  };
  const renameCategory = async (c) => {
    if (!catEditingName.trim() || catEditingName.trim() === c.name) { setCatEditingId(null); return; }
    setCatBusy(true);
    try {
      await updateCategory(c.id, { name: catEditingName.trim() });
      setCatEditingId(null);
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setCatBusy(false); }
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Διαγραφή κατηγορίας "${c.name}";`)) return;
    setCatBusy(true);
    try {
      await deleteCategory(c.id, false);
      toast.success('Διαγράφηκε');
      load(true);
    } catch (err) {
      const n = err.response?.data?.product_count;
      if (n) {
        if (window.confirm(`${err.response.data.error}\n\nΝα διαγραφεί και τα ${n} προϊόντα να πάνε στο "Χωρίς κατηγορία";`)) {
          try {
            const r = await deleteCategory(c.id, true);
            toast.success(r.data.message || 'Διαγράφηκε');
            load(true);
          } catch (e2) { toast.error(e2.response?.data?.error || 'Σφάλμα'); }
        }
      } else {
        toast.error(err.response?.data?.error || 'Σφάλμα');
      }
    } finally { setCatBusy(false); }
  };
  const openCatShops = (c) => {
    const hidden = c.hidden_shop_ids || [];
    setCatShopFor(c);
    setCatShopIds(shops.filter((s) => !hidden.includes(s.id)).map((s) => s.id));
  };
  const saveCatShops = async () => {
    if (!catShopFor) return;
    setCatBusy(true);
    try {
      await updateCategory(catShopFor.id, {
        hidden_shop_ids: shops.filter((s) => !catShopIds.includes(s.id)).map((s) => s.id),
      });
      toast.success('Αποθηκεύτηκε');
      setCatShopFor(null);
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setCatBusy(false); }
  };

  // ── Product drag ──────────────────────────────────────────
  // Swapping happens only once the pointer crosses the MIDPOINT of the row
  // it is over. Swapping on mere overlap makes two differently-sized rows
  // trade places forever while the mouse sits still.
  const onDragStart = (e, p) => {
    if (!canDrag) return;
    dragItem.current = p;
    dragBackup.current = null;
    dropDone.current = false;
    setDraggingId(p.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const onDragOverProduct = (e, target) => {
    if (!canDrag || !dragItem.current) return;
    e.preventDefault();
    e.stopPropagation();
    const dragged = dragItem.current;
    if (dragged.id === target.id) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const below = e.clientY > rect.top + rect.height / 2;

    setItems((prev) => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const from = prev.findIndex((x) => x.id === dragged.id);
      let to = prev.findIndex((x) => x.id === target.id);
      if (from === -1 || to === -1) return prev;

      const moved = { ...prev[from], category_id: target.category_id };
      const next = [...prev];
      next.splice(from, 1);
      to = next.findIndex((x) => x.id === target.id);
      next.splice(below ? to + 1 : to, 0, moved);
      dragItem.current = moved;
      return next;
    });
  };

  // Dropping onto the header puts the product at the top of that category —
  // the only way to reach a category that has no rows to aim at.
  const onDragOverHeader = (e, cat) => {
    if (!canDrag || !dragItem.current) return;
    e.preventDefault();
    const dragged = dragItem.current;
    if (dragged.category_id === cat.id) return;

    setItems((prev) => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const from = prev.findIndex((x) => x.id === dragged.id);
      if (from === -1) return prev;
      const moved = { ...prev[from], category_id: cat.id };
      const next = [...prev];
      next.splice(from, 1);
      const firstOfCat = next.findIndex((x) => x.category_id === cat.id);
      next.splice(firstOfCat === -1 ? next.length : firstOfCat, 0, moved);
      dragItem.current = moved;
      return next;
    });
  };

  const onDrop = (e) => { e.preventDefault(); dropDone.current = true; };

  const onDragEnd = async () => {
    const dropped = dropDone.current;
    const backup = dragBackup.current;
    setDraggingId(null);
    dragItem.current = null;
    dropDone.current = false;
    dragBackup.current = null;
    if (!dropped) { if (backup) setItems(backup); return; }

    const ordered = regroup(items, categories);
    setItems(ordered);
    try {
      await reorderProducts(ordered.map((p) => ({ id: p.id, category_id: p.category_id ?? null })));
    } catch {
      toast.error('Σφάλμα σειράς');
      if (backup) setItems(backup);
    }
  };

  // ── Category drag ─────────────────────────────────────────
  const onCatDragStart = (e, c) => {
    if (!canDrag || c.virtual) return;
    dragCat.current = c;
    catBackup.current = null;
    catDropDone.current = false;
    setDraggingCatId(c.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onCatDragOver = (e, target) => {
    if (!canDrag || !dragCat.current || target.virtual) return;
    e.preventDefault();
    const dragged = dragCat.current;
    if (dragged.id === target.id) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const below = e.clientY > rect.top + rect.height / 2;

    setCategories((prev) => {
      if (!catBackup.current) catBackup.current = [...prev];
      const from = prev.findIndex((x) => x.id === dragged.id);
      let to = prev.findIndex((x) => x.id === target.id);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [m] = next.splice(from, 1);
      to = next.findIndex((x) => x.id === target.id);
      next.splice(below ? to + 1 : to, 0, m);
      return next;
    });
  };
  const onCatDrop = (e) => { e.preventDefault(); catDropDone.current = true; };
  const onCatDragEnd = async () => {
    const dropped = catDropDone.current;
    const backup = catBackup.current;
    setDraggingCatId(null);
    dragCat.current = null;
    catDropDone.current = false;
    catBackup.current = null;
    if (!dropped) { if (backup) setCategories(backup); return; }
    try {
      await reorderCategories(categories.map((c) => c.id));
      setItems((prev) => regroup(prev, categories));
    } catch {
      toast.error('Σφάλμα σειράς');
      if (backup) setCategories(backup);
    }
  };

  const renderFactoriesInline = (facs) => {
    if (!facs || facs.length === 0) return <span style={{ color: 'var(--red)' }}>χωρίς εργοστάσιο</span>;
    return facs.map((f, i) => (
      <span key={f.id}>
        {i > 0 && ', '}
        {f.name}
        {i === 0 && facs.length > 1 && <span style={{ color: 'var(--blue)', fontSize: 11, marginLeft: 2 }}> (default)</span>}
      </span>
    ));
  };

  const hiddenShopNames = (ids) => (ids || [])
    .map((id) => (shops.find((s) => s.id === id) || {}).name)
    .filter(Boolean).join(', ');

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div><h2>🥐 Προϊόντα</h2><p>Κατηγορίες, εργοστάσια και διαθεσιμότητα ανά κατάστημα</p></div>
          <div className="flex-row">
            <input
              className="form-input"
              placeholder="🔍 Προϊόν, κωδικός ή κατηγορία..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240, padding: '6px 12px', margin: 0 }}
            />
            <select value={factoryFilter} onChange={(e) => setFactoryFilter(e.target.value)} className="form-select" style={{ width: 190, padding: '6px 10px' }}>
              <option value="">Όλα τα εργοστάσια</option>
              {factories.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-secondary" onClick={() => setAllOpen(true)} title="Άνοιγμα όλων">⊞</button>
            <button className="btn btn-secondary" onClick={() => setAllOpen(false)} title="Κλείσιμο όλων">⊟</button>
            <button className="btn btn-secondary" onClick={() => setShowCatModal(true)}>🗂 Κατηγορίες</button>
            <button className="btn btn-primary" onClick={() => openCreate()} disabled={factories.length === 0}>+ Νέο Προϊόν</button>
          </div>
        </div>
      </div>

      <div className="page-body">
        {factories.length === 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <p style={{ margin: 0, color: 'var(--gray-600)' }}>⚠️ Δημιουργήστε πρώτα ένα εργοστάσιο για να μπορείτε να καταχωρήσετε προϊόντα.</p>
            </div>
          </div>
        )}

        {!canDrag && !loading && (
          <div style={{
            marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          }}>
            ℹ️ Η αλλαγή σειράς είναι διαθέσιμη μόνο όταν βλέπετε όλα τα ενεργά προϊόντα —
            καθαρίστε την αναζήτηση, το φίλτρο εργοστασίου και το «Ανενεργά».
          </div>
        )}

        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : groups.length === 0 || (search.trim() && visibleCount === 0) ? (
          <div className="card"><div className="card-body">
            <div className="empty-state"><div>🥐</div>
              <p>{search.trim() ? 'Δεν βρέθηκαν προϊόντα' : 'Δεν υπάρχουν προϊόντα'}</p>
            </div>
          </div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {groups.map(({ cat, products }) => {
              const isCatDragging = draggingCatId === cat.id;
              const hidden = hiddenShopNames(cat.hidden_shop_ids);
              const ckey = catKeyOf(cat);
              // While searching, whatever matched is opened for you — a search
              // that shows only closed headers would look like no results.
              const isOpen = search.trim() ? true : !!openCats[ckey];
              return (
                <div
                  key={cat.id === null ? 'uncat' : cat.id}
                  onDragOver={(e) => (dragCat.current ? onCatDragOver(e, cat) : undefined)}
                  onDrop={onCatDrop}
                  style={{ opacity: isCatDragging ? 0.5 : 1 }}
                >
                  {/* Category header — also a drop target, so a product can be
                      moved into a category that currently has no rows */}
                  <div
                    onDragOver={(e) => (dragItem.current ? onDragOverHeader(e, cat) : undefined)}
                    onDrop={onDrop}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 8,
                      background: isCatDragging ? '#dcfce7' : 'var(--gray-100)',
                      border: '1px solid var(--gray-200)',
                      marginBottom: 6,
                    }}
                  >
                    {canDrag && !cat.virtual ? (
                      <span
                        draggable
                        onDragStart={(e) => onCatDragStart(e, cat)}
                        onDragEnd={onCatDragEnd}
                        style={{ ...dragHandleStyle, color: isCatDragging ? '#22c55e' : 'var(--gray-400)' }}
                      >⠿</span>
                    ) : <span style={{ width: 16 }} />}
                    {/* The whole strip toggles, which is a far easier target on
                        a tablet than a small chevron */}
                    <button
                      onClick={() => setOpen(ckey, !isOpen)}
                      disabled={!!search.trim()}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                        background: 'transparent', border: 'none', padding: 0,
                        font: 'inherit', textAlign: 'left',
                        cursor: search.trim() ? 'default' : 'pointer',
                      }}
                    >
                      <span style={{ color: 'var(--gray-500)', fontSize: 13, width: 12 }}>
                        {isOpen ? '▾' : '▸'}
                      </span>
                      <span style={{ fontWeight: 700, color: 'var(--gray-900)', fontSize: 15 }}>
                        {cat.virtual ? '📦 ' : '🗂 '}{cat.name}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {products.length} {products.length === 1 ? 'προϊόν' : 'προϊόντα'}
                      </span>
                      {hidden && (
                        <span style={{ fontSize: 11, color: '#b45309' }}>· 🏪 όχι σε {hidden}</span>
                      )}
                    </button>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!cat.virtual && (
                        <button className="btn btn-secondary btn-sm" title="Διαθεσιμότητα σε καταστήματα"
                          onClick={() => openCatShops(cat)}>🏪</button>
                      )}
                      <button className="btn btn-secondary btn-sm" title="Νέο προϊόν σε αυτή την κατηγορία"
                        onClick={() => { setOpen(ckey, true); openCreate(cat.id); }}>+</button>
                    </div>
                  </div>

                  {!isOpen ? null : products.length === 0 ? (
                    <div
                      onDragOver={(e) => (dragItem.current ? onDragOverHeader(e, cat) : undefined)}
                      onDrop={onDrop}
                      style={{
                        padding: 14, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13,
                        background: 'white', border: '1px dashed var(--gray-200)', borderRadius: 8,
                      }}
                    >
                      {search.trim() ? 'Καμία αντιστοιχία σε αυτή την κατηγορία' : 'Άδεια κατηγορία — σύρετε εδώ ένα προϊόν'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {products.map((p) => {
                        const isDragging = draggingId === p.id;
                        const pHidden = hiddenShopNames(p.hidden_shop_ids);
                        return (
                          <div key={p.id}
                            onDragOver={(e) => (dragItem.current ? onDragOverProduct(e, p) : undefined)}
                            onDrop={onDrop}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                              border: '1px solid var(--gray-100)', borderRadius: 8,
                              background: isDragging ? '#dcfce7' : (p.is_active ? 'white' : 'var(--gray-50)'),
                              opacity: isDragging ? 0.5 : (p.is_active ? 1 : 0.6),
                            }}>
                            {canDrag ? (
                              <span draggable onDragStart={(e) => onDragStart(e, p)} onDragEnd={onDragEnd}
                                style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
                            ) : <span style={{ width: 16 }} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {p.code && (
                                  <span style={{
                                    fontSize: 11, fontWeight: 700, color: 'var(--gray-500)',
                                    background: 'var(--gray-100)', borderRadius: 4, padding: '1px 6px',
                                  }}>{p.code}</span>
                                )}
                                {p.name}
                                {!p.is_active && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>(ανενεργό)</span>}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                                🏭 {renderFactoriesInline(p.factories)} · €{parseFloat(p.price).toFixed(2)}
                                {pHidden && <span style={{ color: '#b45309', marginLeft: 6 }}>· 🏪 όχι σε {pHidden}</span>}
                              </div>
                            </div>
                            <div className="flex-row">
                              {p.is_active ? (
                                <>
                                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>✏️</button>
                                  <button className="btn btn-danger btn-sm" onClick={() => softDelete(p)}>🗑</button>
                                </>
                              ) : (
                                <>
                                  <button className="btn btn-secondary btn-sm" onClick={() => reactivate(p)} title="Επαναφορά">↻</button>
                                  <button className="btn btn-danger btn-sm" onClick={() => hardDelete(p)} title="Οριστική Διαγραφή">❌</button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Product modal ──────────────────────────────────── */}
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Προϊόντος' : 'Νέο Προϊόν'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Όνομα *</label>
                <input className="form-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
              </div>

              <div className="form-group">
                <label className="form-label">Κωδικός</label>
                <input className="form-input" value={form.code} maxLength={30}
                  placeholder="π.χ. 176 — προαιρετικό"
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 0' }}>
                  Βοηθάει στην αναζήτηση. Αφήστε το κενό αν δεν χρειάζεται.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Κατηγορία</label>
                <select className="form-select" value={form.category_id}
                  onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}>
                  <option value="">📦 Χωρίς κατηγορία</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Εργοστάσια *</label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                  Επιλέξτε ένα ή περισσότερα. Το πρώτο στη λίστα (default) θα χρησιμοποιείται αυτόματα όταν το κατάστημα προσθέτει το προϊόν στο καλάθι.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                  {factories.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν εργοστάσια.</div>
                  ) : (() => {
                    const checked = form.factory_ids.map((id) => factories.find((f) => f.id === id)).filter(Boolean);
                    const unchecked = factories.filter((f) => !form.factory_ids.includes(f.id));
                    return [...checked, ...unchecked].map((f) => {
                      const isChecked = form.factory_ids.includes(f.id);
                      const ci = isChecked ? form.factory_ids.indexOf(f.id) : -1;
                      return (
                        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                            <input type="checkbox" checked={isChecked} onChange={() => toggleFactory(f.id)} />
                            {f.name}
                            {ci === 0 && <span className="badge badge-blue" style={{ marginLeft: 6 }}>default</span>}
                          </label>
                          {isChecked && ci > 0 && (
                            <button type="button" className="btn btn-secondary btn-sm"
                              onClick={() => moveFactoryUp(f.id)} title="Μετακίνηση προς τα πάνω"
                              style={{ padding: '2px 8px', fontSize: 12 }}>↑</button>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Τιμή (€) *</label>
                <input className="form-input" type="number" step="0.01" min="0" value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Διαθέσιμο στα καταστήματα</label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                  Όλα επιλεγμένα = το προϊόν φαίνεται παντού. Νέα καταστήματα θα το βλέπουν αυτόματα.
                  Αν η κατηγορία του είναι κρυμμένη από ένα κατάστημα, το προϊόν δεν φαίνεται εκεί ούτως ή άλλως.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                  {shops.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν καταστήματα.</div>
                  ) : (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)', paddingBottom: 6 }}>
                        <input type="checkbox" checked={form.shop_ids.length === shops.length}
                          onChange={(e) => setForm((f) => ({ ...f, shop_ids: e.target.checked ? shops.map((s) => s.id) : [] }))} />
                        Όλα τα καταστήματα
                      </label>
                      {shops.map((s) => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                          <input type="checkbox" checked={form.shop_ids.includes(s.id)} onChange={() => toggleShop(s.id)} />
                          {s.name}
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Category management ────────────────────────────── */}
      {showCatModal && (
        <div onClick={() => setShowCatModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>🗂 Κατηγορίες</span>
              <button onClick={() => setShowCatModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 10px' }}>
                Η σειρά αλλάζει σύροντας τις κατηγορίες στην κύρια λίστα.
              </p>
              {categories.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>Δεν υπάρχουν κατηγορίες ακόμη.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                  {categories.map((c) => (
                    <div key={c.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      border: '1px solid var(--gray-100)', borderRadius: 8,
                    }}>
                      {catEditingId === c.id ? (
                        <>
                          <input className="form-input" value={catEditingName} autoFocus
                            onChange={(e) => setCatEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') renameCategory(c); if (e.key === 'Escape') setCatEditingId(null); }}
                            style={{ flex: 1, padding: '4px 8px', margin: 0 }} />
                          <button className="btn btn-primary btn-sm" disabled={catBusy} onClick={() => renameCategory(c)}>✓</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setCatEditingId(null)}>✕</button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 14 }}>
                            {c.name}
                            <span style={{ color: 'var(--gray-400)', fontSize: 12, marginLeft: 6 }}>
                              ({c.product_count})
                            </span>
                          </span>
                          <button className="btn btn-secondary btn-sm" title="Μετονομασία"
                            onClick={() => { setCatEditingId(c.id); setCatEditingName(c.name); }}>✏️</button>
                          <button className="btn btn-secondary btn-sm" title="Καταστήματα"
                            onClick={() => { setShowCatModal(false); openCatShops(c); }}>🏪</button>
                          <button className="btn btn-danger btn-sm" title="Διαγραφή" disabled={catBusy}
                            onClick={() => removeCategory(c)}>🗑</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="Όνομα νέας κατηγορίας" value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                  style={{ flex: 1, margin: 0 }} />
                <button className="btn btn-primary" disabled={catBusy || !newCatName.trim()} onClick={addCategory}>+ Προσθήκη</button>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowCatModal(false)}>Κλείσιμο</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Category shop visibility ───────────────────────── */}
      {catShopFor && (
        <div onClick={() => setCatShopFor(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 460, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', fontWeight: 600, flexShrink: 0 }}>
              🏪 {catShopFor.name} — διαθεσιμότητα
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 10px' }}>
                Ξετσεκάρετε ένα κατάστημα για να κρύψετε ΟΛΟΚΛΗΡΗ την κατηγορία από εκεί —
                μαζί με όλα τα προϊόντα της, ανεξάρτητα από τις δικές τους ρυθμίσεις.
                Νέα καταστήματα τη βλέπουν αυτόματα.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                {shops.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν καταστήματα.</div>
                ) : (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)', paddingBottom: 6 }}>
                      <input type="checkbox" checked={catShopIds.length === shops.length}
                        onChange={(e) => setCatShopIds(e.target.checked ? shops.map((s) => s.id) : [])} />
                      Όλα τα καταστήματα
                    </label>
                    {shops.map((s) => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={catShopIds.includes(s.id)}
                          onChange={() => setCatShopIds((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])} />
                        {s.name}
                      </label>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setCatShopFor(null)}>Άκυρο</button>
              <button className="btn btn-primary" disabled={catBusy} onClick={saveCatShops}>Αποθήκευση</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
